import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraDataTable, SpecraEmptyState, SpecraEntityCell, SpecraInlineBanner, SpecraLink, SpecraListPage, SpecraPageHeader, SpecraPagination, SpecraTabBar } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecordBreadcrumbs } from '../../components/RecordShell/useRecordBreadcrumbs';
import { renderRouterLink } from '../../lib/renderRouterLink';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { growthCrumbs } from '../GrowthHub/growthCrumbs';
import { useListPursuits } from './hooks';
import { NewPursuitDialog } from './NewPursuitDialog';
const LIFECYCLES = [
    'live',
    'parked',
    'closed'
];
function parseLifecycle(v) {
    return LIFECYCLES.includes(v) ? v : 'live';
}
function RefCell({ value }) {
    if (!value?.label) return /*#__PURE__*/ _jsx(_Fragment, {
        children: "—"
    });
    if (!value.href) return /*#__PURE__*/ _jsx(_Fragment, {
        children: value.label
    });
    return /*#__PURE__*/ _jsx(SpecraLink, {
        href: value.href,
        renderLink: renderRouterLink,
        children: value.label
    });
}
function plural(n, one, many) {
    return `${n} ${n === 1 ? one : many}`;
}
/** "{n} RFI response(s) · {n} proposal(s)" or "—". */ export function workspacesLabel(row) {
    const parts = [];
    if (row.rfiResponseCount > 0) parts.push(plural(row.rfiResponseCount, 'RFI response', 'RFI responses'));
    if (row.proposalCount > 0) parts.push(plural(row.proposalCount, 'proposal', 'proposals'));
    return parts.length ? parts.join(' · ') : '—';
}
const COLUMNS = [
    {
        id: 'name',
        header: 'Name',
        cell: (row)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                title: /*#__PURE__*/ _jsx(SpecraLink, {
                    href: row.href,
                    renderLink: renderRouterLink,
                    children: row.name
                }),
                description: row.splitFromName ? `split from ${row.splitFromName}` : undefined
            })
    },
    {
        id: 'customer',
        header: 'Customer',
        cell: (row)=>/*#__PURE__*/ _jsx(RefCell, {
                value: row.customer
            })
    },
    {
        id: 'office',
        header: 'Contracting office',
        cell: (row)=>/*#__PURE__*/ _jsx(RefCell, {
                value: row.contractingOffice
            })
    },
    {
        id: 'vehicle',
        header: 'Vehicle',
        cell: (row)=>/*#__PURE__*/ _jsx(RefCell, {
                value: row.vehicle
            })
    },
    {
        id: 'holder',
        header: 'Holder now',
        cell: (row)=>row.holder?.label || '—'
    },
    {
        id: 'labels',
        header: 'Labels',
        cell: (row)=>/*#__PURE__*/ _jsx("div", {
                className: "flex flex-wrap gap-1",
                children: row.labels.map((l)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: l.tone || 'neutral',
                        children: l.label
                    }, l.key))
            })
    },
    {
        id: 'workspaces',
        header: 'Workspaces',
        cell: (row)=>workspacesLabel(row)
    },
    {
        id: 'owner',
        header: 'Owner',
        cell: (row)=>row.owner?.displayName || '—'
    }
];
const EMPTY = {
    live: 'No live pursuits yet',
    parked: 'Nothing parked',
    closed: 'Nothing closed'
};
/**
 * GRW-14: the Pursuits list (mockup Pursuits.png) over GRW-11 `ListPursuits`.
 * Rendered at /capture when growth_record_shell_v1 is on (PursuitsListRoute).
 * Labels come from the server only (§3.1); the raw stage is never shown.
 */ export function PursuitsListPage() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const lifecycle = parseLifecycle(params.get('lifecycle'));
    const [tokens, setTokens] = useState([
        ''
    ]);
    const page = tokens.length;
    const query = useListPursuits({
        lifecycle,
        pageToken: tokens[page - 1] ?? ''
    });
    const setShowHeader = useLayoutStore((s)=>s.setShowHeader);
    useEffect(()=>{
        setShowHeader(false);
        return ()=>setShowHeader(true);
    }, [
        setShowHeader
    ]);
    useRecordBreadcrumbs(growthCrumbs({
        label: 'Pursuits'
    }));
    const data = query.data;
    const counts = data?.lifecycleCounts ?? {};
    const next = data?.nextPageToken ?? '';
    const error = query.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
        tone: "danger",
        variant: "framed",
        "data-testid": Testids.pursuitsList.error,
        action: /*#__PURE__*/ _jsx(SpecraButton, {
            variant: "secondary",
            size: "sm",
            "data-testid": Testids.pursuitsList.retry,
            onClick: ()=>query.refetch(),
            children: "Retry"
        }),
        children: "Pursuits could not be loaded."
    }) : undefined;
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.capture.listRoot,
        children: /*#__PURE__*/ _jsx(SpecraListPage, {
            padding: "page",
            header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
                titleSize: "ui",
                title: "Pursuits",
                description: "Deal threads for the bids you are chasing, from first signal to submission",
                actions: /*#__PURE__*/ _jsx(NewPursuitDialog, {})
            }),
            toolbar: /*#__PURE__*/ _jsx(SpecraTabBar, {
                label: "Pursuits by lifecycle",
                value: lifecycle,
                onValueChange: (v)=>{
                    setTokens([
                        ''
                    ]);
                    setParams({
                        lifecycle: v
                    }, {
                        replace: true
                    });
                },
                options: [
                    {
                        value: 'live',
                        label: 'Live',
                        count: counts.live,
                        testId: Testids.pursuitsList.tabLive
                    },
                    {
                        value: 'parked',
                        label: 'Parked',
                        count: counts.parked,
                        testId: Testids.pursuitsList.tabParked
                    },
                    {
                        value: 'closed',
                        label: 'Closed',
                        count: counts.closed,
                        testId: Testids.pursuitsList.tabClosed
                    }
                ]
            }),
            footer: page > 1 || next ? /*#__PURE__*/ _jsx(SpecraPagination, {
                page: page,
                pageCount: next ? page + 1 : page,
                itemLabel: "pursuits",
                onPageChange: (p)=>{
                    if (p > page && next) setTokens([
                        ...tokens,
                        next
                    ]);
                    else if (p < page) setTokens(tokens.slice(0, p));
                }
            }) : undefined,
            children: /*#__PURE__*/ _jsx(SpecraDataTable, {
                columns: COLUMNS,
                rows: data?.rows ?? [],
                loading: query.isLoading,
                error: error,
                getRowId: (r)=>r.captureProjectId,
                testId: Testids.capture.listTable,
                rowTestId: Testids.capture.listRow,
                onRowClick: (r)=>navigate(`/capture/${r.captureProjectId}`),
                getRowActionLabel: (r)=>`Open ${r.name}`,
                empty: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    "data-testid": Testids.pursuitsList.empty,
                    title: EMPTY[lifecycle],
                    description: lifecycle === 'live' ? 'Use New Pursuit to open a deal thread.' : undefined
                })
            })
        })
    });
}
